<template>
  <div ref="captchaContainer"></div>
</template>
<script setup>
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useHead } from '#app';

useHead({
  link: [
    {
      rel: 'preconnect',
      href: 'https://www.google.com',
    },
    {
      rel: 'preconnect',
      href: 'https://www.gstatic.com',
      crossorigin: '',
    },
  ],
});

const props = defineProps({
  reset: {
    type: Boolean,
    default: false,
  },
});

const envPropsStore = useEnvPropsStore();
const { googleEnterpriseRecaptchaSiteKey } = storeToRefs(envPropsStore);

const sitekey = ref('');
const widgetId = ref(null);
const captchaContainer = ref(null);
const scriptLoaded = ref(false);
const observer = ref(null);

const resetCaptcha = () => {
  if (window?.grecaptcha?.enterprise && widgetId.value !== null) {
    window.grecaptcha.enterprise.reset(widgetId.value);
  }
};

const prepareScript = () => {
  return new Promise((resolve) => {
    const src = `https://www.google.com/recaptcha/enterprise.js?render=${sitekey.value}`;
    const existing = document.querySelector(`script[src="${src}"]`);

    if (existing) {
      scriptLoaded.value = true;
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      scriptLoaded.value = true;
      resolve();
    };
    script.onerror = () => {
      console.error('Failed to load reCAPTCHA script');
      resolve();
    };
    document.head.appendChild(script);
  });
};

const initializeCaptcha = async () => {
  sitekey.value = googleEnterpriseRecaptchaSiteKey.value;

  await prepareScript();
};

const setupIntersectionObserver = () => {
  if (!captchaContainer.value) return;

  observer.value = new IntersectionObserver(
    async (entries) => {
      if (entries[0].isIntersecting && !scriptLoaded.value) {
        await initializeCaptcha();
        observer.value.unobserve(captchaContainer.value);
      }
    },
    {
      rootMargin: '50px',
    },
  );

  observer.value.observe(captchaContainer.value);
};

watch(
  () => props.reset,
  () => {
    resetCaptcha();
  },
);

onMounted(() => {
  setupIntersectionObserver();
});

onUnmounted(() => {
  if (observer.value && captchaContainer.value) {
    observer.value.unobserve(captchaContainer.value);
  }
  resetCaptcha();
});
</script>
